/**
 * SlashMenu — block-type picker, Notion-style.
 *
 * Working actions: Story, Epic. Other entries (headings, callout, todo,
 * divider, AI actions) are previewed as disabled rows so the menu reads as
 * a real "command palette" and signals capability that's coming. Disabled
 * rows can't be picked by mouse or keyboard.
 *
 * Keyboard: ↑/↓ moves cursor (skips disabled), Enter/Tab picks, Esc closes.
 */
import { useEffect, useMemo, useRef, useState } from 'react'

export type BlockType = 'story' | 'epic'

type ItemAction =
  | { kind: 'create'; type: BlockType }
  | { kind: 'soon'; tag: string }

interface Item {
  id: string
  label: string
  sub: string
  icon: string
  action: ItemAction
  group: string
}

const ITEMS: Item[] = [
  // Requirements
  { id: 'story', group: 'Requirements', label: 'Story', sub: 'A user-facing requirement', icon: '•',  action: { kind: 'create', type: 'story' } },
  { id: 'epic',  group: 'Requirements', label: 'Epic',  sub: 'A group of related stories', icon: '#', action: { kind: 'create', type: 'epic'  } },
  // Structure (preview)
  { id: 'h2',       group: 'Structure', label: 'Heading', sub: 'Section break in the document',  icon: 'H',  action: { kind: 'soon', tag: 'soon' } },
  { id: 'divider',  group: 'Structure', label: 'Divider', sub: 'Horizontal separator',           icon: '—',  action: { kind: 'soon', tag: 'soon' } },
  { id: 'callout',  group: 'Structure', label: 'Callout', sub: 'A highlighted note or warning',  icon: '!',  action: { kind: 'soon', tag: 'soon' } },
  { id: 'todo',     group: 'Structure', label: 'Todo',    sub: 'A free-floating checklist item', icon: '☐',  action: { kind: 'soon', tag: 'soon' } },
  // AI (preview)
  { id: 'ai-criteria', group: 'AI',     label: 'Draft acceptance criteria', sub: 'Skerpa generates from the story description', icon: '✦', action: { kind: 'soon', tag: 'soon' } },
  { id: 'ai-split',    group: 'AI',     label: 'Split this story',          sub: 'Suggest 2–3 narrower stories',                 icon: '✦', action: { kind: 'soon', tag: 'soon' } },
  { id: 'ai-persona',  group: 'AI',     label: 'Suggest persona link',      sub: 'Match this story to a known persona',          icon: '✦', action: { kind: 'soon', tag: 'soon' } },
]

interface Props {
  /** Anchor rect to position the menu. */
  anchor: { top: number; left: number } | null
  /** Optional filter string (from slash-query in an input). */
  filter?: string
  onPick: (type: BlockType) => void
  onClose: () => void
}

export function SlashMenu({ anchor, filter = '', onPick, onClose }: Props) {
  const [cursor, setCursor] = useState(0)
  const ref = useRef<HTMLDivElement>(null)

  const q = filter.trim().toLowerCase().replace(/^\//, '')
  const filtered = useMemo(() => {
    if (!q) return ITEMS
    return ITEMS.filter(i => i.label.toLowerCase().includes(q) || i.id.includes(q))
  }, [q])

  // Map cursor index to first selectable (non-soon) item
  const selectableIndices = useMemo(
    () => filtered.map((it, i) => (it.action.kind === 'create' ? i : -1)).filter(i => i >= 0),
    [filtered],
  )
  const safeCursor = selectableIndices.includes(cursor)
    ? cursor
    : (selectableIndices[0] ?? -1)

  useEffect(() => { setCursor(selectableIndices[0] ?? 0) }, [q, selectableIndices])

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current) return
      if (ref.current.contains(e.target as Node)) return
      onClose()
    }
    function moveCursor(delta: 1 | -1) {
      const idxInList = selectableIndices.indexOf(safeCursor)
      const next = selectableIndices[(idxInList + delta + selectableIndices.length) % selectableIndices.length]
      if (next !== undefined) setCursor(next)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowDown') { e.preventDefault(); moveCursor(1); return }
      if (e.key === 'ArrowUp')   { e.preventDefault(); moveCursor(-1); return }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const item = filtered[safeCursor]
        if (item && item.action.kind === 'create') { e.preventDefault(); onPick(item.action.type) }
        return
      }
      if (e.key === 'Escape') { e.preventDefault(); onClose() }
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [filtered, safeCursor, selectableIndices, onPick, onClose])

  if (!anchor || filtered.length === 0) return null

  // Group by category for rendering, preserving filtered order
  const byGroup: Array<{ group: string; entries: Array<{ item: Item; index: number }> }> = []
  filtered.forEach((item, index) => {
    const last = byGroup[byGroup.length - 1]
    if (last && last.group === item.group) last.entries.push({ item, index })
    else byGroup.push({ group: item.group, entries: [{ item, index }] })
  })

  return (
    <div
      ref={ref}
      role="listbox"
      aria-label="Insert block"
      className="absolute z-50 rounded-lg p-1"
      style={{
        position: 'fixed',
        top: Math.max(8, anchor.top - window.scrollY),
        left: Math.max(8, anchor.left - window.scrollX),
        minWidth: 280,
        maxHeight: 'min(420px, 80vh)',
        overflowY: 'auto',
        background: 'var(--color-bg-card)',
        border: '1px solid var(--color-border)',
        boxShadow: '0 8px 24px -8px rgba(29,26,21,0.18), 0 2px 6px -2px rgba(29,26,21,0.06)',
      }}
    >
      {byGroup.map((g, gi) => (
        <div key={g.group} className={gi === 0 ? '' : 'mt-1'}>
          <div
            className="px-2.5 pt-1.5 pb-1"
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--color-fg-muted)',
            }}
          >
            {g.group}
          </div>
          {g.entries.map(({ item, index }) => {
            const disabled = item.action.kind === 'soon'
            const active = index === safeCursor && !disabled
            return (
              <button
                key={item.id}
                role="option"
                aria-selected={active}
                aria-disabled={disabled}
                disabled={disabled}
                onMouseEnter={() => { if (!disabled) setCursor(index) }}
                onMouseDown={(e) => {
                  if (disabled) return
                  e.preventDefault()
                  if (item.action.kind === 'create') onPick(item.action.type)
                }}
                className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded text-left transition-colors"
                style={{
                  background: active ? 'var(--color-warm-100)' : 'transparent',
                  cursor: disabled ? 'default' : 'pointer',
                  opacity: disabled ? 0.5 : 1,
                  border: 0,
                }}
              >
                <span
                  className="inline-flex items-center justify-center font-mono"
                  style={{
                    width: 28, height: 28, borderRadius: 6,
                    background: 'var(--color-warm-100)',
                    border: '1px solid var(--color-border)',
                    color: g.group === 'AI' ? 'var(--color-solution-700)' : 'var(--color-fg-secondary)',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  {item.icon}
                </span>
                <span className="flex-1 min-w-0">
                  <span
                    className="block"
                    style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--color-fg)' }}
                  >
                    {item.label}
                  </span>
                  <span
                    className="block truncate"
                    style={{ fontSize: 11.5, color: 'var(--color-fg-muted)' }}
                  >
                    {item.sub}
                  </span>
                </span>
                {disabled && (
                  <span
                    className="font-mono flex-shrink-0"
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      padding: '2px 6px',
                      borderRadius: 4,
                      background: 'var(--color-warm-100)',
                      color: 'var(--color-fg-muted)',
                    }}
                  >
                    Soon
                  </span>
                )}
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}
